import { ActionReducerMapBuilder, createSlice, PayloadAction } from '@reduxjs/toolkit'
import type { ConsumableItemType, PackingItemType } from 'features/Order/services/packers'
import { withLoadingReducer } from 'utils/reducerHandler'
import { OrderLineItemsType } from '../PopupCheckQTY/slice'
import { fetchPackingDetail, fetchPackingDetailRemovable } from './thunk'

export type PopupPackingDetailInitialStateType = {
  order: {
    order_id: string
    type: string
    invoice: string
    status: string
    kitchen_status: string
  }
  timestamp: {
    payment_verified_at: number
  }
  items: PackingItemType[]
  consumables: ConsumableItemType[]
  removableProducts: PackingItemType[]
  misc: {
    total_relabeled_item: number
    note: string
    gift: {
      sender_name: string
      receiver_name: string
      note: string
    }
  }
  selectedItemRowIndex: number | null
  selectedItem: PackingItemType | null
  canToggle: number[]
  fulfillmentId: string
  isWithoutPackaging: boolean
  tempNotes: { id: number; note: string; note_id: string; status_id: number }[]
}

const initialState: PopupPackingDetailInitialStateType = {
  order: {
    order_id: '',
    type: '',
    invoice: '',
    status: '',
    kitchen_status: '',
  },
  timestamp: {
    payment_verified_at: 0,
  },
  items: [],
  consumables: [],
  removableProducts: [],
  misc: {
    total_relabeled_item: 0,
    note: '',
    gift: {
      sender_name: '',
      receiver_name: '',
      note: '',
    },
  },
  selectedItemRowIndex: null,
  selectedItem: null,
  canToggle: [],
  fulfillmentId: '',
  isWithoutPackaging: false,
  tempNotes: [],
}

// ------------------------------------------
// parse old order line item to fit new modifier type
export const parseOldModifierToNewModifier = (
  listItem: OrderLineItemsType['add_ons'] | OrderLineItemsType['modifiers'],
) => {
  if (!listItem || !Array.isArray(listItem)) return []
  return listItem.map((modifier) => ({
    id: '',
    name: modifier.name,
    variants:
      modifier.modifier_variants?.map((variant) => ({
        id: variant.id?.toString() || '',
        name: variant.name,
        modifier_type: '',
        change_type: '',
        price_change: 0,
      })) || [],
  }))
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getProductInfo = (item: any) => ({
  id: item.product_id?.toString() || item.order_product_id?.toString() || '',
  name: item.product_name || item.order_product_name || '',
  sku: item.sku || item.order_product_sku || '',
  brand_name: item.brand_name || '',
  image_url: item.product_images?.[0] || item.order_line_item_product_image || '',
  bundle_product_id: '',
  has_kitchen: false,
})

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getInventoryInfo = (item: any) => ({
  rack: item.rack_name || item.order_product_rack || '',
  order_qty: item.total_quantity || item.order_product_quantity || 0,
  scanned_qty: item.order_product_scanned_quantity || 0,
  note_id: item.note?.toString() || '',
  note: item.product_note?.displayName || '',
  status_id: 0,
})

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const mapToPackingItem = (item: any): PackingItemType => {
  const lineItem = item.removable_items?.[0] || {}

  return {
    id: lineItem.order_line_item_id || item.order_line_item_id,
    monolith_id: '',
    type: '',
    kitchen_item_id: lineItem.item_id || item.item_id,
    special_type: item.special_type,
    product: getProductInfo(item),
    inventory: getInventoryInfo(item),
    details: [
      {
        id: lineItem.item_id?.toString() || item.item_id?.toString() || '',
        qty: lineItem.quantity || item.order_product_actual_quantity || 0,
      },
    ],
    modifiers: parseOldModifierToNewModifier(lineItem.modifiers || item.modifiers),
    add_ons: parseOldModifierToNewModifier(lineItem.add_ons || item.add_ons),
    misc: {
      is_manual_input_sku: !!item.is_manual_input_sku,
      is_need_to_relabel: !!item.is_need_to_relabel,
    },
  }
}

// parse old response to fit new PackingItemType
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const parseRemovableProductsToPackingItemType = (items: any[]): PackingItemType[] => {
  if (!items || !Array.isArray(items)) return []
  return items.map(mapToPackingItem)
}

const PopupPackingDetailSlice = createSlice({
  name: 'popupPackingDetail',
  initialState,
  reducers: {
    setPackingDetail: (state, action) => {
      state.order = action.payload.order
      state.timestamp = action.payload.timestamp
      state.items = action.payload.items
      state.consumables = action.payload.consumables
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const apiGift = (action.payload.misc?.gift || {}) as any
      state.misc = {
        ...action.payload.misc,
        gift: {
          sender_name: apiGift.sender_name || '',
          receiver_name: apiGift.receiver_name || '',
          note: apiGift.note || '',
        },
      }
    },
    setSelectedItem: (state, action) => {
      state.selectedItem = action.payload
      state.selectedItemRowIndex = null
    },
    setSelectedItemRow: (state, action: PayloadAction<number>) => {
      state.selectedItemRowIndex = action.payload
      state.selectedItem = state.items[action.payload]
    },
    setItems: (state, action) => {
      state.items = action.payload
    },
    setTemporaryNote: (state, action) => {
      const copyStateItem = [...state.items]
      copyStateItem.forEach((item) => {
        const copyItem = { ...item }
        if (item.id === action.payload.id) {
          copyItem.inventory.note = action.payload.note
          copyItem.inventory.note_id = action.payload.note_id
          copyItem.inventory.status_id = action.payload.status_id
        }
      })
      state.items = copyStateItem
      state.tempNotes = [...state.tempNotes, action.payload]
    },
    setIsWIthoutPackaging: (state, action) => {
      state.isWithoutPackaging = action.payload
    },
    setRemovableProducts: (state, action) => {
      state.removableProducts = action.payload
    },
    resetState: () => initialState,
  },
  extraReducers: withLoadingReducer(
    (builder: ActionReducerMapBuilder<PopupPackingDetailInitialStateType>) => {
      builder.addCase(fetchPackingDetail.fulfilled, (state, action) => {
        if (action.payload) {
          state.order = action.payload.order
          state.timestamp = action.payload.timestamp

          state.consumables = action.payload.consumables
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const apiGift = (action.payload.misc?.gift || {}) as any
          state.misc = {
            ...action.payload.misc,
            gift: {
              sender_name: apiGift.sender_name || '',
              receiver_name: apiGift.receiver_name || '',
              note: apiGift.note || '',
            },
          }

          state.canToggle = action.payload.items
            .filter(
              (item: { misc: { is_manual_input_sku: boolean } }) => item.misc.is_manual_input_sku,
            )
            .map((item: { id: number }) => item.id)
          state.fulfillmentId = action.payload.fulfillment_id

          // map notes temporary to fit new PackingItemType
          state.items = action.payload.items.map((item) => {
            const tempNote = state.tempNotes.find((tempNote) => tempNote.id === item.id)
            return {
              ...item,
              inventory: {
                ...item.inventory,
                note: tempNote?.note,
                note_id: tempNote?.note_id,
                status_id: tempNote?.status_id,
              },
            }
          })
        }
      })
      builder.addCase(fetchPackingDetailRemovable.fulfilled, (state, action) => {
        if (action.payload) {
          state.removableProducts = parseRemovableProductsToPackingItemType(
            action.payload.data.removable_products,
          )
        }
      })
    },
    [fetchPackingDetail, fetchPackingDetailRemovable],
  ),
})

export const {
  setPackingDetail,
  setSelectedItem,
  setSelectedItemRow,
  resetState,
  setItems,
  setTemporaryNote,
  setIsWIthoutPackaging,
  setRemovableProducts,
} = PopupPackingDetailSlice.actions
export default PopupPackingDetailSlice.reducer
